<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import type { ConfigModule, ModuleCategory } from '~/types/config';
import ConfigFormTabs from '../base/forms/ConfigFormTabs.vue';
import UnsavedChangesDialog from '~/components/base/dialogs/UnsavedChangesDialog.vue';
import SaveFormDialog from '~/components/base/dialogs/SaveFormDialog.vue';
import PendingDeletionsDialog from '~/components/base/dialogs/PendingDeletionsDialog.vue';
import MissingFieldsDialog from '~/components/base/dialogs/MissingFieldsDialog.vue';
import ConfigChangesDialog from '~/components/base/dialogs/ConfigChangesDialog.vue';
import { useI18n } from 'vue-i18n';
import { useUnsavedChangesGuard } from '~/composables/forms/unsavedChanges';
import { useToast } from "primevue/usetoast";
import { useConfigStore } from '~/stores/config';
import { useConfigParserStore } from '~/stores/configParser';
import { useFocusNext } from '~/composables/forms/useFocusNext';
import { useFormValidation } from '~/composables/forms/useFormValidation';
import { DeleteCategories } from '~/Common/DeleteCategories';
import { useConfigChanges } from '~/Common/useConfigChanges';
import { useAppLayout } from '~/composables/useAppLayout';

const { t, locale } = useI18n();

const props = withDefaults(
  defineProps<{
    configData: Record<string, ConfigModule> | null;
    configHash: string;
    loadingConfig: boolean;
  }>(),
  {
  }
);

const emit = defineEmits<{
  'cancel-config-edit': [];
  'reload-config': [{ resolve: () => void; reject: (error: Error) => void }];
}>();

const toast = useToast();
const configStore = useConfigStore();
const configParserStore = useConfigParserStore();
const { isCollapsed } = useAppLayout();


const languageCode = computed(() => locale.value);

const deletionsDialogVisible = ref(false);
const missingFieldsDialogVisible = ref(false);
const changesDialogVisible = ref(false);
const saveFormVisible = ref(false);

const confirmType = ref<'edit' | 'delete'>('edit');
const configTitle = computed(() => props.configHash || '');

const updateConfigLoading = ref(false);

watch(isCollapsed, (collapsed) => {
  if (collapsed) {
    document.body.classList.add('app-sidebar-collapsed');
  } else {
    document.body.classList.remove('app-sidebar-collapsed');
  }
});


const deleteCategories = new DeleteCategories({
  onCategoryDeleted: (category: ModuleCategory) => {
    // Mark category as deleted in store
    configParserStore.markCategoryAsDeleted(category);
  },
  onCategoryRestored: (category: ModuleCategory) => {
    // Restore category in store
    configParserStore.restoreCategory(category);
  },
  applyDeletion: async (xpath: string, locale: string) => {
    configStore.deleteConnection(xpath, locale);
  }
});
const deletionsCount = computed(() => deleteCategories.deletedCategoriesCount.value);
const pendingDeletions = computed(() => deleteCategories.deletedModulesArray.value);


// Use required fields from store as computed
const { isSubmitDisabled, getMissingRequiredFields, areRequiredFieldsFilled } = useFormValidation(
  computed(() => configParserStore.getRequiredFields)
);
const missingRequiredFieldsInModules = computed(() => {
  return configParserStore.getFieldsByModule(getMissingRequiredFields.value);
});
const missingRequiredFields = computed(() => missingRequiredFieldsInModules.value);
const missingFieldsCount = computed(() => getMissingRequiredFields.value.length);


// Use store's debounced values for changes calculation
const { changes, hasChanges, changesCount } = useConfigChanges(
  deleteCategories
);

// Update hasUnsavedChanges to include actual changes
const hasActualChanges = computed(() => hasChanges.value || deleteCategories.hasDeletedCategories.value);
const unsavedChanges = useUnsavedChangesGuard(updateDeleteConfigOperation);


const { focusNext, handleEnterKey } = useFocusNext();
const focusNextId = ref<string>('');

onMounted(() => {
  document.addEventListener('keyup', handleEnterKeyWrapper);
  const cleanupGuard = unsavedChanges.setupGuard();
  unsavedChanges.trackChanges([
    computed(() => configParserStore.getConfigValues),
    deleteCategories.hasDeletedCategories,
    hasActualChanges // If there are actual changes, we consider it unsaved
  ]);

  return () => {
    cleanupGuard();
  };
});

onUnmounted(() => {
  document.removeEventListener('keyup', handleEnterKeyWrapper);
});

// Load the configuration file data whenever the language code changes
watch(languageCode, async () => {
  await handleReloadConfig();
});


watch(() => focusNextId.value, (newId) => {
  if (newId) {
    focusNext(newId);
  }
});

/**
 * Wrapper for handleEnterKey to provide form-specific options
 */
function handleEnterKeyWrapper(event: KeyboardEvent) {
  handleEnterKey(event, {
    requiredFields: configParserStore.getRequiredFields,
    dialogOpen: saveFormVisible.value || unsavedChanges.unsavedChangesDialog.value,
  });
}

/**
 * Handles the form submission.
 * Validates required fields and shows a confirmation dialog if there are unsaved changes.
 */
async function handleSubmit() {
  // Prevent form submission if required fields are empty
  if (!areRequiredFieldsFilled.value) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.errors.missingInputs'),
      detail: t('common.toast.errors.fillRequiredFields'),
      life: 3000
    });
    return;
  }

  if (unsavedChanges.hasUnsavedFormInput.value) {
    showConfirmation('edit');
  }
  else {
    updateDeleteConfigOperation(true); // Save and close
  }
}

/**
 * Show confirmation dialog for edit or delete action.
 */
function showConfirmation(type: 'edit' | 'delete') {
  if (type === 'edit') {
    confirmType.value = 'edit';
    saveFormVisible.value = true;
    return;
  }

  if (type === 'delete') {
    toast.add({
      severity: 'info',
      summary: t('common.toast.info'),
      detail: 'Delete action is not implemented yet.',
      life: 3000
    });
  }
}

function handleCancel() {
  emit('cancel-config-edit');
}

/**
 * Handles the Apply action - saves changes without closing the form.
 */
async function handleApply() {
  // Prevent apply if required fields are empty
  if (!areRequiredFieldsFilled.value) {
    toast.add({
      severity: 'error',
      summary: t('common.toast.errors.missingInputs'),
      detail: t('common.toast.errors.fillRequiredFields'),
      life: 3000
    });
    return;
  }

  updateDeleteConfigOperation(false); // Don't close after save
}

/**
 * Update configuration operation that saves the changes made in the form and applies pending deletions.
 * @param closeAfterSave - Whether to close the form after saving (default: true for backward compatibility)
 */
function updateDeleteConfigOperation(closeAfterSave: boolean = true) {
  if (updateConfigLoading.value) return;

  updateConfigLoading.value = true;

  const saveChanges = buildSaveChangesFromUiChanges();
  const hasDeletions = deleteCategories.hasDeletedCategories.value;
  const currentLocale = locale.value;

  console.log('[SAVE] changes sent to GraphQL', saveChanges);

  // IMPORTANT:
  // Start the existing store GraphQL call BEFORE closing/cancelling/unmounting.
  const savePromise = Promise.resolve()
    .then(async () => {
      if (saveChanges.length > 0) {
        await configStore.updateConfigValues(saveChanges, currentLocale);
      }

      if (hasDeletions) {
        await deleteCategories.applyAllDeletions(currentLocale);
      }
    })
    .then(() => {
      resetFormState();

      toast.add({
        severity: 'success',
        summary: t('common.toast.success'),
        detail: t('config.toast.saved'),
        life: 3000
      });
    })
    .catch((error) => {
      console.error('Error while saving configuration:', error);

      toast.add({
        severity: 'error',
        summary: t('common.toast.error'),
        detail: t('config.toast.saveError'),
        life: 3000
      });
    })
    .finally(() => {
      updateConfigLoading.value = false;
    });

  saveFormVisible.value = false;
  unsavedChanges.unsavedChangesDialog.value = false;

  if (closeAfterSave) {
    handleCancel();
  }

  void savePromise;
}
function buildSaveChangesFromUiChanges(): { id: number; elementValue: string }[] {
  const result: { id: number; elementValue: string }[] = [];

  changes.value.forEach(module => {
    Object.values(module.categories).forEach(category => {
      category.headlineGroups.forEach(group => {
        group.elements.forEach(element => {
          if (!element.id) return;

          result.push({
            id: element.id,
            elementValue: element.elementValue ?? '',
          });
        });
      });
    });
  });

  return result;
}


/**
 * Resets the form state by clearing all unsaved changes, config values, required fields and hidden tabs.
 */
function resetFormState() {
  configParserStore.clearRequiredElements();
  deleteCategories.restoreAllCategories();
  configParserStore.resetConfigValues();
  unsavedChanges.resetUnsavedChanges?.();
  configParserStore.clearDeletedCategories();
}

/**
 * Handle the reload config event that should be triggered after deletion
 */
async function handleReloadConfig() {
  try {
    // Extract xpath and values from changes for updateConfigValues
    const changesForUpdate: Record<string, string> = {};
    changes.value.forEach(module => {
      Object.values(module.categories).forEach(category => {
        category.headlineGroups.forEach(group => {
          group.elements.forEach(element => {
            if (element.elementPath) {
              changesForUpdate[element.elementPath] = element.elementValue || '';
            }
          });
        });
      });
    });

    const reloadConfigPromise = new Promise<void>((res, rej) => {
      emit('reload-config', { resolve: res, reject: rej });
    });

    reloadConfigPromise.then(() => {
      resetFormState();
      for (const [xpath, value] of Object.entries(changesForUpdate)) {
        configParserStore.updateElementValue(xpath, value);
      }
    }).catch((error) => {
      console.error('Error reloading config:', error);
      toast.add({
        severity: 'error',
        summary: t('common.toast.error'),
        detail: t('config.toast.reloadError'),
        life: 3000
      });
    });
  } catch (error) {
    console.error('Error reloading config:', error);
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.reloadError'),
      life: 3000
    });
  }
}



// Template ref for ConfigFormTabs
const configFormTabsRef = ref<InstanceType<typeof ConfigFormTabs> | null>(null);

/**
 * Handles navigation to a specific category in the configuration form.
 * @param moduleId - The ID of the module to switch to.
 * @param xPath - The XPath of the category or element to navigate to.
 */
const handleNavigateToCategory = ({ moduleId, xPath }: { moduleId: number; xPath: string }) => {
  try {
    // Set the correct module first
    configParserStore.setSelectedModule(moduleId);

    // Switch to the correct tab using the exposed method
    nextTick(() => {
      if (configFormTabsRef.value) {
        configFormTabsRef.value.switchToCategory(xPath);
      }
    });

  } catch (error) {
    console.error('Error navigating to category:', error);
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('common.navigation.navigationError'),
      life: 3000
    });
  }
};

/**
 * Handles navigation to a specific element in the configuration form.
 * This function sets the module, switches to the correct tab, and focuses the specified element.
 * @param moduleId - The ID of the module containing the element.
 * @param xPath - The XPath of the category or element to navigate to.
 * @param elementId - The ID of the specific element to focus on.
 */
const handleNavigateToElement = ({
  moduleId,
  xPath,
  elementId
}: {
  moduleId: number;
  xPath: string;
  elementId: number;
}) => {
  try {
    // Set the correct module
    configParserStore.setSelectedModule(moduleId);

    // Switch to the correct tab and focus element
    nextTick(() => {
      if (configFormTabsRef.value) {
        configFormTabsRef.value.switchToCategory(xPath);

        nextTick(() => {
          focusNext(`input-${elementId}`, 300);
        });
      }
    });

  } catch (error) {
    console.error('Error navigating to element:', error);
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('common.toast.navigationError'),
      life: 3000
    });
  }
};

// Handle undo deletion from dialog
const handleUndoDeletion = (xpath: string) => {
  try {
    deleteCategories.restoreCategory(xpath);

    toast.add({
      severity: 'success',
      summary: t('common.toast.success'),
      detail: t('config.toast.undoDelete'),
      life: 3000
    });
  } catch (error) {
    console.error('Error undoing deletion:', error);
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.undoDeleteError'),
      life: 3000
    });
  }
};

const handleUndoAllDeletions = () => {
  try {
    deleteCategories.restoreAllCategories();

    toast.add({
      severity: 'success',
      summary: t('common.toast.success'),
      detail: t('config.toast.undoDelete'),
      life: 3000
    });
  } catch (error) {
    console.error('Error undoing all deletions:', error);
    toast.add({
      severity: 'error',
      summary: t('common.toast.error'),
      detail: t('config.toast.undoDeleteError'),
      life: 3000
    });
  }
};

</script>

<template>
  <div class="h-full ">
    <form
      @submit.prevent="handleSubmit"
      v-if="!props.loadingConfig"
      class="border-1 border-round-md px-4 config-main-content"
    >
      <div class="grid mb-2">
        <div class="col-12 ">
          <ConfigFormTabs
            ref="configFormTabsRef"
            :config-data="props.configData"
            :delete-categories="deleteCategories"
            @update:focusNextId="focusNext"
            @focus-next="focusNext"
            @reload-config="handleReloadConfig"
          />
          <!-- Footer for submit cancel to convey that it is for the whole form -->
          <div v-if="!props.loadingConfig" class="configform-footer fixed bottom-0 right-0 p-0 w-110">
            <div class="footer-content flex flex-wrap justify-content-between align-items-center gap-3 py-3 pr-7 pl-4">
              <!-- Info Links -->
              <div class="flex gap-3 flex-wrap">
                <Button
                  id="deletions"
                  :label="deletionsCount === 1 ?
                    t('common.dialogs.deletions.count', { count: deletionsCount }) :
                    t('common.dialogs.deletions.countPlural', { count: deletionsCount })"
                  class="w-auto"
                  severity="warn"
                  type="button"
                  @click="deletionsDialogVisible = true"
                />
                <Button
                  id="missingFields"
                  :label="missingFieldsCount === 1 ?
                    t('common.dialogs.missingFields.count', { count: missingFieldsCount }) :
                    t('common.dialogs.missingFields.countPlural', { count: missingFieldsCount })"
                  class="w-auto"
                  severity="danger"
                  type="button"
                  @click="missingFieldsDialogVisible = true"
                />
                <Button
                  id="changes"
                  :label="changesCount === 1 ?
                    t('common.dialogs.changes.count', { count: changesCount }) :
                     t('common.dialogs.changes.countPlural', { count: changesCount })"
                  class="w-auto"
                  severity="info"
                  type="button"
                  @click="changesDialogVisible = true"
                />
              </div>

              <!-- Action Buttons -->
              <div class="flex gap-3">
                <Button
                  id="save"
                  :label="t('config.form.saveAll')"
                  class="w-auto"
                  type="submit"
                  severity="success"
                  :loading="updateConfigLoading"
                  :disabled="isSubmitDisabled"
                />
                <Button
                  id="cancel"
                  :label="t('common.actions.cancel')"
                  class="w-auto"
                  :loading="updateConfigLoading"
                  severity="warning"
                  type="button"
                  @click="handleCancel"
                />
                <Button
                  id="apply"
                  :label="t('common.actions.apply')"
                  class="w-auto"
                  type="button"
                  severity="info"
                  :loading="updateConfigLoading"
                  :disabled="isSubmitDisabled"
                  @click="handleApply"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
    <div
      v-else
      class="w-full h-full flex justify-content-center align-items-center"
    >
      <ProgressSpinner/>
    </div>
  </div>
  <SaveFormDialog
    v-model:visible="saveFormVisible"
    :confirm-type="confirmType"
    :entity-name="t('common.entityNames.config')"
    :entity-display-name="configTitle"
    :has-unsaved-form-input="unsavedChanges.hasUnsavedFormInput"
    :save-loading="updateConfigLoading"
    @save="updateDeleteConfigOperation"
  />

  <UnsavedChangesDialog
    v-model:visible="unsavedChanges.unsavedChangesDialog"
    @discard="unsavedChanges.proceedWithNavigation"
    @save="unsavedChanges.saveChanges"
    @cancel="unsavedChanges.cancelNavigation"
  />

  <PendingDeletionsDialog
    v-model:visible="deletionsDialogVisible"
    :deletions="pendingDeletions"
    @undo-deletion="handleUndoDeletion"
    @restore-all-deletions="handleUndoAllDeletions"
    @navigate-to-category="handleNavigateToCategory"
  />

  <MissingFieldsDialog
    v-model:visible="missingFieldsDialogVisible"
    :missing-fields="missingRequiredFields"
    @navigate-to-element="handleNavigateToElement"
  />

  <ConfigChangesDialog
    v-model:visible="changesDialogVisible"
    :changes="changes"
    @navigate-to-field="handleNavigateToElement"
  />
</template>

<style scoped>
.config-header {
  border-bottom: 1px solid hsl(var(--border));
}

.config-title {
  color: hsl(var(--foreground));
  line-height: 1.2;
  text-transform: capitalize;
}

.configform-footer {
  background: hsl(var(--background)) !important;
  border-top: 1px solid hsl(var(--border)) !important;
  box-shadow: 0 -4px 16px rgba(0,0,0,0.10);
  transition: left 0.3s ease;
  z-index: 100;
}

/* Expanded sidebar state */
.configform-footer {
  left: 0;
}

.config-main-content {
  background-color: hsl(var(--background)) !important;
  position: relative;
  /* Add padding bottom to prevent footer overlap */
  padding-bottom: 120px !important;
  min-height: 100%;
}

:deep(.border-1) {
  border: 1px solid hsl(var(--border)) !important;
}

/* Mobile Responsive Styles */
@media (max-width: 1280px) {
  .config-title {
    font-size: 1.25rem;
  }

  .configform-footer {
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    position: fixed !important;
    bottom: 0 !important;
    padding: 0 !important;
  }

  .footer-content {
    flex-direction: column !important;
    align-items: stretch !important;
    padding: 0.75rem !important;
    gap: 0.5rem !important;
  }

  .footer-content > div {
    width: 100%;
  }

  .footer-content .flex {
    flex-wrap: wrap;
    justify-content: center;
  }

  .footer-content button {
    flex: 1;
    min-width: 120px;
  }

  .config-main-content {
    padding-bottom: 180px !important;
  }

  /* Make form fields stack vertically */
  :deep(.config-main-content .grid),
  :deep(.config-main-content .p-grid),
  :deep(.config-main-content .p-formgrid),
  :deep(.config-main-content .p-field),
  :deep(.config-main-content .field) {
    display: flex !important;
    flex-direction: column !important;
    gap: 0.5rem;
    align-items: stretch;
  }

  :deep(.config-main-content [class*="col-"]),
  :deep(.config-main-content .p-col),
  :deep(.config-main-content .p-col-12),
  :deep(.config-main-content .p-col-6),
  :deep(.config-main-content .p-col-4),
  :deep(.config-main-content .p-col-3) {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
    padding: 0.5rem 0 !important;
  }

  /* Full width form inputs */
  :deep(.config-main-content .p-inputtext),
  :deep(.config-main-content .p-dropdown),
  :deep(.config-main-content .p-calendar),
  :deep(.config-main-content .p-inputnumber),
  :deep(.config-main-content .p-inputtextarea),
  :deep(.config-main-content .p-multiselect),
  :deep(.config-main-content .p-chips) {
    width: 100% !important;
  }

  /* Break the flex container that holds label and field side by side */
  :deep(.config-main-content .p-accordioncontent > div) {
    display: block !important;
  }

  /* Force the field wrapper (flex:1 container) to stack vertically */
  :deep(.config-main-content .p-accordioncontent > div > div) {
    display: block !important;
    width: 100% !important;
    flex: none !important;
    margin-bottom: 1rem !important;
  }

  /* Stack label and input vertically */
  :deep(.config-main-content .p-field),
  :deep(.config-main-content .field),
  :deep(.config-main-content .p-formgrid > .p-field),
  :deep(.config-main-content .p-formgrid > .field) {
    display: block !important;
    width: 100% !important;
  }

  :deep(.config-main-content .p-field > label),
  :deep(.config-main-content .field > label),
  :deep(.config-main-content label) {
    display: block !important;
    width: 100% !important;
    margin: 0 0 0.5rem 0 !important;
    line-height: 1.2;
    position: static !important;
    transform: none !important;
    pointer-events: auto;
    float: none !important;
  }

  :deep(.config-main-content .p-field > :not(label)),
  :deep(.config-main-content .field > :not(label)) {
    display: block !important;
    width: 100% !important;
  }

  :deep(.config-main-content .p-inputwrapper),
  :deep(.config-main-content .p-inputwrapper-filled),
  :deep(.config-main-content .p-inputwrapper-focus) {
    position: static !important;
    //display: block !important;
  }

  :deep(.config-main-content .p-inputgroup),
  :deep(.config-main-content .p-input-icon-left),
  :deep(.config-main-content .p-input-icon-right) {
    position: static !important;
    display: block !important;
    width: 100% !important;
  }

  /* Adjust accordion and tabs for mobile */
  :deep(.p-accordion),
  :deep(.p-tabview) {
    width: 100%;
  }

  :deep(.p-accordion-header),
  :deep(.p-tabview-nav) {
    flex-wrap: wrap;
  }
}

/* Tablet adjustments */
@media (max-width: 1024px) and (min-width: 769px) {
  .config-title {
    font-size: 1.5rem;
  }

  .configform-footer {
    left: 0;
    padding: 0 0.5rem;
  }

  .config-main-content {
    padding-bottom: 100px;
  }
}

/* Small mobile devices */
@media (max-width: 768px) {
  .config-title {
    font-size: 1.1rem;
  }

  .footer-content {
    padding: 0.5rem !important;
  }

  .footer-content button {
    font-size: 0.875rem;
    padding: 0.5rem 0.75rem;
  }

  .config-main-content {
    padding-bottom: 220px !important;
  }
}
</style>
